/**
 * Pure ordinal-extraction for video / audio variant histories.
 *
 * `getVariantLabel` (display) and `buildClipDownloadFilename` (download
 * filename) both consume the integers returned here, so the visible chip and
 * the saved filename can never disagree on numbering.
 *
 * No React, no DOM imports — safe to use from any module.
 */

import type {
  AssetRef,
  BasicAssetMetadata,
  GeneratedAssetMetadata,
} from '@ponpon/api-client'

/**
 * Audio-remix entries carry extra runtime keys on `metadata` (`type`,
 * `source_original_index`, etc.) that the generated `GeneratedAssetMetadata`
 * shape does not model. Intersect with the known optional keys so we can read
 * them without type casts.
 */
export type RemixMetadata = GeneratedAssetMetadata & {
  type?: string
  timestamp?: number
  source_original_index?: number
  sourceOriginalIndex?: number
  source_original_path?: string
  sourceOriginalPath?: string
}

/**
 * Loose result shape that subsumes every history entry shape in the codebase:
 * - `GeneratedAssetEntry.result: BasicAsset` (videoGenerator / imageGenerator /
 *   soundExpert) has `id: string` required.
 * - `CharacterAsset.images.main.history[i].result` (and the location/object
 *   equivalents) only has `local_path?: string`.
 * Making every field optional accepts both.
 */
export type VideoHistoryResult = {
  id?: string
  local_path?: string
  metadata?: BasicAssetMetadata
}

export interface VideoHistoryRecord {
  user_prompt?: string
  result?: VideoHistoryResult
  refs?: AssetRef[]
  metadata?: RemixMetadata
}

export type VideoHistoryEntry = VideoHistoryRecord | null

export function metadataType(metadata: RemixMetadata | undefined): string | undefined {
  return metadata?.type
}

export function metadataNumber(
  metadata: RemixMetadata | undefined,
  ...keys: Array<'source_original_index' | 'sourceOriginalIndex'>
): number | undefined {
  if (!metadata) return undefined
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }
  return undefined
}

export function metadataString(
  metadata: RemixMetadata | undefined,
  ...keys: Array<'source_original_path' | 'sourceOriginalPath'>
): string | undefined {
  if (!metadata) return undefined
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'string' && value.length > 0) return value
  }
  return undefined
}

export interface VariantOrdinals {
  /** 1-based ordinal of this entry among originals (null for remix entries). */
  originalOrdinal: number | null
  /** 1-based ordinal of this remix within its source-original (null when not a remix). */
  remixOrdinal: number | null
  /** Ordinal of the original this remix descends from; null when no source can be resolved. */
  sourceOriginalOrdinal: number | null
}

/**
 * Extract ordinals for entry `realIdx` from a video/audio variant history.
 *
 * Pure integer logic — display ("v1 remix 1") and filename ("v01_remix_01") are
 * both built from these integers.
 */
export function getVariantOrdinals(history: VideoHistoryEntry[], realIdx: number): VariantOrdinals {
  const originalOrdinals = new Map<number, number>()
  let originalCount = 0

  for (let i = 0; i < history.length; i++) {
    const entry = history[i]
    if (!entry?.result) continue
    const isRemix = metadataType(entry?.metadata) === 'audio_remix'
    if (!isRemix) {
      originalCount++
      originalOrdinals.set(i, originalCount)
    }
  }

  const resolveRemixSourceIndex = (idx: number): number | null => {
    const entry = history[idx]
    const sourceIdx = metadataNumber(entry?.metadata, 'source_original_index', 'sourceOriginalIndex')
    if (sourceIdx != null && originalOrdinals.has(sourceIdx)) {
      return sourceIdx
    }
    const sourcePath = metadataString(entry?.metadata, 'source_original_path', 'sourceOriginalPath')
    if (sourcePath) {
      const matchingIndex = history.findIndex(
        (candidate) =>
          Boolean(candidate?.result) &&
          metadataType(candidate?.metadata) !== 'audio_remix' &&
          candidate?.result?.local_path === sourcePath,
      )
      if (matchingIndex >= 0) return matchingIndex
    }
    for (let i = idx - 1; i >= 0; i--) {
      if (originalOrdinals.has(i)) return i
    }
    return null
  }

  const entry = history[realIdx]
  const isRemix = metadataType(entry?.metadata) === 'audio_remix'

  if (isRemix) {
    const sourceIndex = resolveRemixSourceIndex(realIdx)
    const sourceOrdinal = sourceIndex != null ? originalOrdinals.get(sourceIndex) ?? null : null
    let remixCount = 0
    for (let i = 0; i <= realIdx; i++) {
      const candidate = history[i]
      if (!candidate?.result) continue
      if (metadataType(candidate?.metadata) !== 'audio_remix') continue
      if (resolveRemixSourceIndex(i) === sourceIndex) remixCount++
    }
    return {
      originalOrdinal: null,
      remixOrdinal: Math.max(1, remixCount),
      sourceOriginalOrdinal: sourceOrdinal,
    }
  }

  return {
    originalOrdinal: originalOrdinals.get(realIdx) ?? null,
    remixOrdinal: null,
    sourceOriginalOrdinal: null,
  }
}
